import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { combineLatest, filter, Observable, map, BehaviorSubject, switchMap, Subject, tap, catchError, of, throwError, from } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { getLocation } from 'src/app/shared/constants/locations.constant';
import { getQuality } from 'src/app/shared/constants/qualities.constant';
import { MarketFlip, MarketOrder, FlipsStatus, MarketFlipHistoryStats, MarketFlipRecord, FlipperResponse, FlipsCounter, UserFlipsResponse, FlipLocationStatus, FlipperStats } from 'src/app/shared/models/flipper.model';
import { JsonReaderService } from '../json-reader/json-reader.service';
import { AfmStats, TopItem } from 'src/app/shared/models/top-items.model';
import { Location } from 'src/app/shared/models/location.model';
import { isPlatformServer } from '@angular/common';
import { environment } from 'src/environments/environment';
import { AfmLogsService } from '../afm-logs/afm-logs.service';
import { CraftGroupPersistenceModel, DoneCraftPersistanceModel, SaveCraftGroupResponse } from 'src/app/shared/models/craft-models/craft-persistence-models';
import { SpecLevel } from 'src/app/shared/models/craft-models/specs-models';

@Injectable({
  providedIn: 'root',
})
export class TopItemsService {
  private url: string = environment.afmApis.topItems.baseUrl;
  private renewedFlipsOrTopItemsSubject = new Subject<void>();
  public renewedFlipsOrTopItems$ = this.renewedFlipsOrTopItemsSubject.asObservable();

  constructor(
    private jsonReaderService: JsonReaderService,
    private http: HttpClient,
    private afmLogsService: AfmLogsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  //MARK: Helper methods
  // Helper method to ensure JsonReader is loaded
  private async ensureJsonReaderLoaded(): Promise<void> {
    const isLoaded = await firstValueFrom(this.jsonReaderService.hasLoaded$);
    if (!isLoaded) {
      await this.jsonReaderService.ensureLoaded();
    }
  }

  private mapFlipRecord(item: any): MarketFlipRecord {
    let flip = new MarketFlipRecord();
    flip.id = item.id;
    flip.soldItem = this.jsonReaderService.getItemFromIndex(item.itemTypeId);
    flip.boughtItem = this.jsonReaderService.getItemFromIndex(item.boughtItemTypeId);
    flip.flipTime = new Date(item.flipTime + 'Z');
    flip.buyLocation = getLocation(item.buyLocationId);
    flip.buyQuality = getQuality(item.buyQuality);
    flip.buyPrice = item.buyPrice;
    flip.sellLocation = getLocation(item.sellLocationId);
    flip.sellQuality = getQuality(item.sellQuality);
    flip.sellPrice = item.sellPrice;
    flip.isUpgrade = item.isUpgrade;
    flip.upgradeCost = item.upgradeCost;
    flip.smugglersFee = item.smugglersFee;
    flip.profitPremium = item.profitPremium;
    flip.profitNonPremium = item.profitNonPremium;
    flip.server = item.server == 0 ? 'west' : item.server == 1 ? 'east' : item.server == 2 ? 'europe' : 'unknown';
    flip.failedFlip = item.failedFlip;
    return flip;
  }

  private mapFlipStatus(item: any): FlipsStatus {
    return {
      server: item.server,
      totalFlips: item.totalFlips,
      totalFlipsConsumed: item.totalFlipsConsumed,
      totalBuyOrders: item.totalBuyOrders,
      totalBuyOrdersConsumed: item.totalBuyOrdersConsumed,
      totalSellOrders: item.totalSellOrders,
      totalSellOrdersConsumed: item.totalSellOrdersConsumed,
      totalPremiumProfit: item.totalPremiumProfit,
      totalPremiumProfitConsumed: item.totalPremiumProfitConsumed,
    };
  }

  //MARK: Flipper API methods
  getFlips(server: string, checkEntitlements: boolean, userFlips: boolean): Observable<FlipperResponse> {
    this.afmLogsService.logSiteUse('User requested flips from Flipper API');

    return from(this.ensureJsonReaderLoaded()).pipe(
      switchMap(() => {
        this.renewedFlipsOrTopItemsSubject.next();
        const params = new HttpParams().set('server', server).set('checkEntitlements', checkEntitlements).set('userFlips', userFlips);

        return this.http.get<any>(`${this.url}/flips`, { params }).pipe(
          map((response) => {
            const flipperResponse: FlipperResponse = new FlipperResponse();

            flipperResponse.flips = response.flips.map((item: any) => {
              let flip = new MarketFlip();
              flip.id = item.id;
              flip.itemTypeId = item.itemTypeId;
              flip.createdAt = new Date(item.createdAt);
              flip.item = this.jsonReaderService.getItemFromIndex(item.itemTypeId);

              let buyOrder = new MarketOrder();
              buyOrder.id = item.buyOrder.id;
              buyOrder.item = this.jsonReaderService.getItemFromIndex(item.buyOrder.itemTypeId);
              buyOrder.unitPriceSilver = item.buyOrder.unitPriceSilver;
              buyOrder.amount = item.buyOrder.amount;
              buyOrder.createdAt = new Date(item.buyOrder.createdAt);
              buyOrder.expires = new Date(item.buyOrder.expires);
              buyOrder.enchantmentLevel = item.buyOrder.enchantmentLevel;
              buyOrder.qualityLevel = getQuality(item.buyOrder.qualityLevel);
              buyOrder.itemTypeId = item.buyOrder.itemTypeId;
              buyOrder.location = getLocation(item.buyOrder.location);
              buyOrder.isConsumed = item.buyOrder.isConsumed;
              buyOrder.server = item.buyOrder.server;
              flip.buyOrder = buyOrder;

              let sellOrder = new MarketOrder();
              sellOrder.id = item.sellOrder.id;
              sellOrder.item = this.jsonReaderService.getItemFromIndex(item.sellOrder.itemTypeId);
              sellOrder.unitPriceSilver = item.sellOrder.unitPriceSilver;
              sellOrder.amount = item.sellOrder.amount;
              sellOrder.createdAt = new Date(item.sellOrder.createdAt);
              sellOrder.expires = new Date(item.sellOrder.expires);
              sellOrder.enchantmentLevel = item.sellOrder.enchantmentLevel;
              sellOrder.qualityLevel = getQuality(item.sellOrder.qualityLevel);
              sellOrder.itemTypeId = item.sellOrder.itemTypeId;
              sellOrder.location = getLocation(item.sellOrder.location);
              sellOrder.isConsumed = item.sellOrder.isConsumed;
              sellOrder.server = item.sellOrder.server;
              flip.sellOrder = sellOrder;

              flip.server = flip.buyOrder.server === flip.sellOrder.server ? flip.buyOrder.server : undefined;
              flip.isUpgrade = flip.buyOrder.enchantmentLevel > flip.sellOrder.enchantmentLevel;
              flip.upgradeFrom = flip.sellOrder.enchantmentLevel;
              flip.upgradeTo = flip.buyOrder.enchantmentLevel;

              let upgradeResources = item.upgradeResources.map((resource: any) => {
                let upgradeResource = {
                  uniqueName: resource['@uniquename'],
                  item: this.jsonReaderService.getItemFromIndex(resource['@uniquename']),
                  count: resource['@count'],
                  price: resource.price,
                };
                return upgradeResource;
              });
              flip.upgradeResources = upgradeResources;

              return flip;
            });
            flipperResponse.missedFlipsCount = response.missedFlipsCount;
            flipperResponse.missedFlipsProfit = response.missedFlipsProfit;

            return flipperResponse;
          })
        );
      })
    );
  }

  getFlipsStatusLive(flipsLocationStatsTimeHours: number = 1): Observable<FlipperStats> {
    return from(this.ensureJsonReaderLoaded()).pipe(
      switchMap(() => {
        this.renewedFlipsOrTopItemsSubject.next();
        const params = new HttpParams().set('flipsLocationStatsTimeHours', flipsLocationStatsTimeHours);

        return this.http.get<any>(`${this.url}/flips/stats/live`, { params }).pipe(
          map((response) => {
            const flipsStatuses: FlipsStatus[] = [];
            const flipLocationStatus: FlipLocationStatus[] = [];

            flipsStatuses.push(...response.flipsStatuses.map((item: any) => this.mapFlipStatus(item)));

            flipLocationStatus.push(
              ...response.flipLocationStatusDTOs.map((item: any) => {
                const stat: FlipLocationStatus = {
                  server: item.server,
                  buyLocation: getLocation(item.locationId),
                  count: item.count,
                  profitPremium: item.profitPremium,
                };
                return stat;
              })
            );

            return { flipsStatus: flipsStatuses, flipLocationsStatus: flipLocationStatus };
          })
        );
      })
    );
  }

  getFlipsStatusHistory(historyStatusTopAmount: number = 5): Observable<MarketFlipHistoryStats[]> {
    return from(this.ensureJsonReaderLoaded()).pipe(
      switchMap(() => {
        this.renewedFlipsOrTopItemsSubject.next();
        const params = new HttpParams().set('historyStatusTopAmount', historyStatusTopAmount);

        return this.http.get<any>(`${this.url}/flips/stats/history`, { params }).pipe(
          map((response) => {
            const marketFlipHistoryStats: MarketFlipHistoryStats[] = [];

            response.forEach((item: any) => {
              let marketFlipHistoryStat = new MarketFlipHistoryStats();
              marketFlipHistoryStat.server = item.server;
              marketFlipHistoryStat.flipCount = item.flipCount;
              marketFlipHistoryStat.totalProfitNonPremium = item.totalProfitNonPremium;
              marketFlipHistoryStat.totalProfitPremium = item.totalProfitPremium;
              marketFlipHistoryStat.topFlips = item.topFlips.map((flipRecord: any) => {
                return this.mapFlipRecord(flipRecord);
              });
              marketFlipHistoryStats.push(marketFlipHistoryStat);
            });

            return marketFlipHistoryStats;
          })
        );
      })
    );
  }

  getUserFlips(amount: number = 100): Observable<UserFlipsResponse> {
    this.afmLogsService.logSiteUse('User requested user flips history from Flipper API');

    return from(this.ensureJsonReaderLoaded()).pipe(
      switchMap(() => {
        const params = new HttpParams().set('amount', amount.toString());

        return this.http.get<any>(`${this.url}/flips/user`, { params }).pipe(
          map((response) => {
            const userFlipsResponse = new UserFlipsResponse();
            userFlipsResponse.flips = response.flips.map((item: any) => this.mapFlipRecord(item));
            userFlipsResponse.flipsCount = response.flipsCount;
            userFlipsResponse.upgradeFlipsCount = response.upgradeFlipsCount;
            userFlipsResponse.normalFlipsCount = response.normalFlipsCount;
            userFlipsResponse.totalProfitPremium = response.totalProfitPremium;
            userFlipsResponse.totalProfitUpgradePremium = response.totalProfitUpgradePremium;
            userFlipsResponse.totalProfitNormalPremium = response.totalProfitNormalPremium;
            return userFlipsResponse;
          })
        );
      })
    );
  }

  deleteUserFlip(id: string): Observable<boolean> {
    this.afmLogsService.logSiteUse('User requested to delete a flip from Flipper API: ' + id);

    return this.http.delete(`${this.url}/flips/user/${id}`, { observe: 'response' }).pipe(
      map((response) => response.status === 200),
      catchError((error) => {
        console.error('Error deleting flip', error);
        return of(false);
      })
    );
  }

  updateFlipFailed(id: string, failed: boolean): Observable<MarketFlipRecord> {
    this.afmLogsService.logSiteUse('User requested to update a flip failed status from Flipper API: ' + id + ' to ' + failed);

    return from(this.ensureJsonReaderLoaded()).pipe(
      switchMap(() => {
        const params = new HttpParams().set('failed', failed.toString());
        return this.http.patch<any>(`${this.url}/flips/failed/${id}`, {}, { params }).pipe(map((item) => this.mapFlipRecord(item)));
      })
    );
  }

  getFlipsCounters(days: number): Observable<FlipsCounter[]> {
    const params = new HttpParams().set('days', days);

    return this.http.get<FlipsCounter[]>(`${this.url}/flips/counters`, { params }).pipe(
      map((counters) =>
        counters.map((counter) => {
          counter.server = counter.server == '0' ? 'west' : counter.server == '1' ? 'east' : counter.server == '2' ? 'europe' : 'unknown';
          counter.dateTime = new Date(counter.dateTime);
          return counter;
        })
      ),
      tap((counters) => {
        counters.sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
      })
    );
  }

  consumeFlip(id: string, upgradeCost: number, smugglersFee: number): Observable<MarketFlip> {
    this.afmLogsService.logSiteUse('User requested to consume a flip from Flipper API: ' + id);

    return from(this.ensureJsonReaderLoaded()).pipe(
      switchMap(() => {
        const params = new HttpParams().set('upgradeCost', Math.round(upgradeCost)).set('smugglersFee', smugglersFee);

        return this.http.patch<MarketFlip>(`${this.url}/flips/consume/${id}`, {}, { params });
      })
    );
  }

  //MARK: AFM STATS API methods
  getAfmStats(): Observable<AfmStats> {
    const url = isPlatformServer(this.platformId) ? 'assets/ssg-files/afm_stats.json' : `${this.url}/afmstats`;

    return this.http.get<AfmStats>(url).pipe(
      catchError((error) => {
        console.error('Error fetching AFM stats:', error);
        return throwError(() => error);
      })
    );
  }

  // MARK: TOP ITEMS API methods
  getTopItems(server: string, location: Location, amount: number, sortBy: 'amount' | 'silver', shopCatSubCats: string | null, startDate: Date | null, endDate: Date | null): Observable<TopItem[]> {
    this.afmLogsService.logSiteUse('User requested top items from AFM API');

    return from(this.ensureJsonReaderLoaded()).pipe(
      switchMap(() => {
        this.renewedFlipsOrTopItemsSubject.next();

        let params = new HttpParams().set('server', server).set('location', location.index).set('amount', amount.toString()).set('sortBy', sortBy);

        if (startDate) {
          params = params.set('startDate', startDate.toISOString().split('T')[0]);
        }
        if (endDate) {
          params = params.set('endDate', endDate.toISOString().split('T')[0]);
        }
        if (shopCatSubCats) {
          params = params.set('shopCatSubCats', shopCatSubCats);
        }

        return this.http.get<any[]>(`${this.url}/topItems`, { params }).pipe(
          map((data) => {
            return data.map((item) => {
              const topItem = new TopItem();
              topItem.item = this.jsonReaderService.getItemFromIndex(item.itemId);
              if (topItem.item === undefined) {
                console.warn('item not found', item.itemId);
              }
              topItem.totalPrice = item.totalPrice;
              topItem.amount = item.amount;
              return topItem;
            });
          })
        );
      })
    );
  }

  // MARK: CRAFT GROUP API methods
  saveCraftGroup(craftGroup: CraftGroupPersistenceModel): Observable<SaveCraftGroupResponse> {
    this.afmLogsService.logSiteUse(`User attempting to save craft group: ${craftGroup.name}`);
    const endpointUrl = `${this.url}/craftGroups/save`;

    return this.http.post<SaveCraftGroupResponse>(endpointUrl, craftGroup).pipe(
      catchError((error) => {
        console.error('Error saving craft group:', error);
        return throwError(() => error);
      })
    );
  }

  getAllCraftGroups(): Observable<CraftGroupPersistenceModel[]> {
    this.afmLogsService.logSiteUse('User requested all craft groups');
    const endpointUrl = `${this.url}/craftGroups/all`;

    return this.http.get<CraftGroupPersistenceModel[]>(endpointUrl).pipe(
      catchError((error) => {
        console.error('Error fetching all craft groups:', error);
        return throwError(() => error);
      })
    );
  }

  deleteCraftGroup(id: string): Observable<string> {
    this.afmLogsService.logSiteUse(`User attempting to delete craft group: ${id}`);
    const endpointUrl = `${this.url}/craftGroups/delete/${id}`;

    return this.http.delete<string>(endpointUrl).pipe(
      catchError((error) => {
        console.error('Error deleting craft group:', error);
        return throwError(() => error);
      })
    );
  }

  // MARK: DONE CRAFTS API methods
  saveDoneCraft(doneCraft: DoneCraftPersistanceModel): Observable<DoneCraftPersistanceModel> {
    this.afmLogsService.logSiteUse(`User attempting to save done craft: ${doneCraft.craftGroup.name}`);
    const endpointUrl = `${this.url}/doneCrafts/save`;

    return this.http.post<DoneCraftPersistanceModel>(endpointUrl, doneCraft).pipe(
      catchError((error) => {
        console.error('Error saving done craft:', error);
        return throwError(() => error);
      })
    );
  }

  getAllDoneCrafts(): Observable<DoneCraftPersistanceModel[]> {
    this.afmLogsService.logSiteUse('User requested all done crafts');
    const endpointUrl = `${this.url}/doneCrafts/all`;

    return this.http.get<DoneCraftPersistanceModel[]>(endpointUrl).pipe(
      catchError((error) => {
        console.error('Error fetching all done crafts:', error);
        return throwError(() => error);
      })
    );
  }
  
  deleteDoneCraft(id: string): Observable<string> {
    this.afmLogsService.logSiteUse(`User attempting to delete done craft: ${id}`);
    const endpointUrl = `${this.url}/doneCrafts/delete/${id}`;

    return this.http.delete<string>(endpointUrl).pipe(
      catchError((error) => {
        console.error('Error deleting done craft:', error);
        return throwError(() => error);
      })
    );
  }

  // MARK: SPECS API methods
  saveUserSpecs(specs: SpecLevel[]): Observable<string> {
    this.afmLogsService.logSiteUse(`User attempting to save specs.`);
    const endpointUrl = `${this.url}/specs/save`;

    return this.http.post<string>(endpointUrl, specs).pipe(
      catchError((error) => {
        console.error('Error saving user specs:', error);
        const errorMessage = error.error?.message || error.message || 'Failed to save specs.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  getAllUserSpecs(): Observable<SpecLevel[]> {
    this.afmLogsService.logSiteUse(`User attempting to get specs.`);
    const endpointUrl = `${this.url}/specs/all`;

    return this.http.get<SpecLevel[]>(endpointUrl).pipe(
      catchError((error) => {
        console.error('Error fetching user specs:', error);
        const errorMessage = error.error?.message || error.message || 'Failed to fetch specs.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }
}
